/**
 * 채팅 말풍선 공통 컴포넌트.
 *
 * 온보딩 챗(Signup.tsx)·메인 챗(Chat.tsx) 양쪽에서 동일한 시각 언어 사용.
 *  - user: 우측 정렬, accent 컬러 배경
 *  - assistant: 좌측 정렬, 흰 surface + 테두리
 *  - typing: 점 3개 애니메이션 (CSS keyframes는 index.css에 정의)
 */
export type ChatRole = 'user' | 'assistant';

interface ChatBubbleProps {
  role: ChatRole;
  text: string;
  /** 보조 텍스트 (예: "Butler") — assistant 위에 작은 라벨로 노출 */
  label?: string;
  /** 말풍선 아래에 추가 노드 (출처 칩 등) */
  footer?: React.ReactNode;
}

export function ChatBubble({ role, text, label, footer }: ChatBubbleProps) {
  const isUser = role === 'user';
  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} w-full`}>
      {!isUser && label && (
        <div className="text-[10px] uppercase tracking-wider text-[var(--accent)] font-semibold mb-[2px] ml-xs">
          {label}
        </div>
      )}
      <div
        className={[
          'max-w-[82%] px-md py-[10px] rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap break-words',
          isUser
            ? 'bg-[var(--accent)] text-[var(--accent-on)] rounded-br-sm'
            : 'bg-[var(--surface)] border border-[var(--border)] text-[var(--text-primary)] rounded-bl-sm',
        ].join(' ')}
      >
        {text}
      </div>
      {footer && <div className={`mt-xs ${isUser ? 'self-end' : 'self-start'} max-w-[82%]`}>{footer}</div>}
    </div>
  );
}

/** 어시스턴트 응답 대기 중 — 회색 말풍선에 점 3개 애니메이션. */
export function TypingBubble({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-start w-full">
      {label && (
        <div className="text-[10px] uppercase tracking-wider text-[var(--accent)] font-semibold mb-[2px] ml-xs">
          {label}
        </div>
      )}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl rounded-bl-sm px-md py-[12px] inline-flex items-center gap-[4px]">
        <span className="typing-dot" />
        <span className="typing-dot" style={{ animationDelay: '0.15s' }} />
        <span className="typing-dot" style={{ animationDelay: '0.3s' }} />
      </div>
    </div>
  );
}
